import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import dayjs from 'dayjs';
import type NetRevenueCostBreakdown from 'lumen/models/net-revenue/cost-breakdown';
import type NetRevenueStayDatePoint from 'lumen/models/net-revenue/stay-date-point';
import type NetRevenueTotals from 'lumen/models/net-revenue/totals';
import {
  DATE_FORMAT,
  STAY_DATE_GRANULARITIES,
  type StayDateGranularity,
} from 'lumen/utils/constants/net-revenue-constants';
import { NORMALIZED_PERIOD } from 'lumen/utils/constants/ri-constant-values';
import {
  COST_BREAKDOWNS,
  type CostBreakdown,
} from 'lumen/utils/net-revenue/cost-breakdowns';
import {
  calculateAdr,
  calculateAverageCostPerRoomNight,
  calculateCostToRoomRevenueRatio,
  calculateNetAdr,
  calculateNetRevenueToRoomRevenueRatio,
  calculateOccupancyRate,
  calculateRelativeChange,
} from 'lumen/utils/net-revenue/metrics';
import type {
  CostBreakdownData,
  CostTypeComparison,
  CostTypeRow,
  Kpi,
  KpiData,
  NetRevenueReference,
  StayDateComparisonPoint,
  StayDateData,
  StayDatePoint,
} from 'lumen/utils/net-revenue/types';

interface StayDateBucket {
  capacity: number;
  comparisonCapacity: number;
  comparisonCost: number;
  comparisonNetRevenue: number;
  comparisonRoomNights: number;
  comparisonRoomRevenue: number;
  cost: number;
  hasComparison: boolean;
  label: string;
  netRevenue: number;
  roomNights: number;
  roomRevenue: number;
}

function buildKpi(
  value: number,
  comparison: number | undefined,
  overrides: Partial<Kpi> = {},
): Kpi {
  return {
    changePercent: calculateRelativeChange(value, comparison),
    invertColors: false,
    value,
    ...overrides,
  };
}

function toStayComparison(
  reference: NetRevenueReference,
  fallbackCapacity: number,
): StayDateComparisonPoint {
  const cost = reference.cost ?? 0;
  return {
    avgCost: calculateAverageCostPerRoomNight(cost, reference.roomNights) ?? 0,
    cost,
    costPercent:
      calculateCostToRoomRevenueRatio(cost, reference.roomRevenue) ?? 0,
    netRevenue: reference.netRevenue,
    occupancy:
      calculateOccupancyRate(
        reference.roomNights,
        reference.capacity ?? fallbackCapacity,
      ) ?? 0,
    roomRevenue: reference.roomRevenue,
  };
}

function toCostComparison(reference: NetRevenueReference): CostTypeComparison {
  const cost = reference.cost ?? 0;
  return {
    adr: calculateAdr(reference.roomRevenue, reference.roomNights) ?? 0,
    avgCost: calculateAverageCostPerRoomNight(cost, reference.roomNights) ?? 0,
    cost,
    costPercent:
      calculateCostToRoomRevenueRatio(cost, reference.roomRevenue) ?? 0,
    netAdr: calculateNetAdr(reference.netRevenue, reference.roomNights) ?? 0,
    netRevenue: reference.netRevenue,
    netRevenuePercent:
      calculateNetRevenueToRoomRevenueRatio(
        reference.netRevenue,
        reference.roomRevenue,
      ) ?? 0,
    roomNights: reference.roomNights,
    roomRevenue: reference.roomRevenue,
  };
}

function toCostTypeRow(
  record: NetRevenueCostBreakdown,
  referenceType: string,
  childrenByParentId: Map<string, NetRevenueCostBreakdown[]>,
): CostTypeRow {
  const reference = record.referenceByType(referenceType);
  const children = (
    record.id == null ? [] : (childrenByParentId.get(record.id) ?? [])
  ).map((child) => toCostTypeRow(child, referenceType, childrenByParentId));
  return {
    adr: record.adr,
    avgCost: record.avgCost,
    children,
    comparison: reference ? toCostComparison(reference) : null,
    cost: record.cost ?? 0,
    costPercent: record.costPercent,
    // Named rows always have a name; '' only guards against malformed data.
    name: record.name ?? '',
    netAdr: record.netAdr,
    netRevenue: record.netRevenue,
    netRevenuePercent: record.netRevenuePercent,
    roomNights: record.roomNights,
    roomRevenue: record.roomRevenue,
  };
}

export default class NetRevenueDashboard extends Model {
  [Type] = 'net-revenue/dashboard' as const;

  @belongsTo('net-revenue/totals', { async: false, inverse: null })
  declare total: NetRevenueTotals;
  @hasMany('net-revenue/cost-breakdown', { async: false, inverse: null })
  declare channel: HasMany<NetRevenueCostBreakdown>;
  @hasMany('net-revenue/cost-breakdown', { async: false, inverse: null })
  declare costProfiles: HasMany<NetRevenueCostBreakdown>;
  @hasMany('net-revenue/cost-breakdown', { async: false, inverse: null })
  declare marketSegment: HasMany<NetRevenueCostBreakdown>;
  @hasMany('net-revenue/stay-date-point', { async: false, inverse: null })
  declare stayDate: HasMany<NetRevenueStayDatePoint>;
  /** Set by the serializer. */
  @attr() declare availableBreakdowns: CostBreakdown[];

  @service declare intl: Services['intl'];

  #bucketStayDate(
    granularity: 'week' | 'month',
    referenceType: string,
  ): StayDatePoint[] {
    const periodKey = NORMALIZED_PERIOD[granularity];
    const bucketsByStart = new Map<string, StayDateBucket>();

    this.stayDate.forEach((point) => {
      const label = dayjs
        .utc(point.date)
        .startOf(periodKey)
        .format(DATE_FORMAT);
      const reference = point.referenceByType(referenceType);
      const bucket = bucketsByStart.get(label) ?? {
        capacity: 0,
        comparisonCapacity: 0,
        comparisonCost: 0,
        comparisonNetRevenue: 0,
        comparisonRoomNights: 0,
        comparisonRoomRevenue: 0,
        cost: 0,
        hasComparison: false,
        label,
        netRevenue: 0,
        roomNights: 0,
        roomRevenue: 0,
      };

      bucket.capacity += point.capacity;
      bucket.cost += point.cost ?? 0;
      bucket.netRevenue += point.netRevenue;
      bucket.roomNights += point.roomNights;
      bucket.roomRevenue += point.roomRevenue;

      if (reference) {
        bucket.hasComparison = true;
        bucket.comparisonCapacity += reference.capacity ?? point.capacity;
        bucket.comparisonCost += reference.cost ?? 0;
        bucket.comparisonNetRevenue += reference.netRevenue;
        bucket.comparisonRoomNights += reference.roomNights;
        bucket.comparisonRoomRevenue += reference.roomRevenue;
      }

      bucketsByStart.set(label, bucket);
    });

    return Array.from(bucketsByStart.values()).map((bucket) => ({
      avgCost:
        calculateAverageCostPerRoomNight(bucket.cost, bucket.roomNights) ?? 0,
      comparison: bucket.hasComparison
        ? {
            avgCost:
              calculateAverageCostPerRoomNight(
                bucket.comparisonCost,
                bucket.comparisonRoomNights,
              ) ?? 0,
            cost: bucket.comparisonCost,
            costPercent:
              calculateCostToRoomRevenueRatio(
                bucket.comparisonCost,
                bucket.comparisonRoomRevenue,
              ) ?? 0,
            netRevenue: bucket.comparisonNetRevenue,
            occupancy:
              calculateOccupancyRate(
                bucket.comparisonRoomNights,
                bucket.comparisonCapacity,
              ) ?? 0,
            roomRevenue: bucket.comparisonRoomRevenue,
          }
        : null,
      cost: bucket.cost,
      costPercent:
        calculateCostToRoomRevenueRatio(bucket.cost, bucket.roomRevenue) ?? 0,
      label: bucket.label,
      netRevenue: bucket.netRevenue,
      occupancy:
        calculateOccupancyRate(bucket.roomNights, bucket.capacity) ?? 0,
      roomRevenue: bucket.roomRevenue,
    }));
  }

  #getBreakdownSource(
    breakdown: CostBreakdown,
  ): HasMany<NetRevenueCostBreakdown> {
    switch (breakdown) {
      case COST_BREAKDOWNS.CHANNEL:
        return this.channel;
      case COST_BREAKDOWNS.MARKET_SEGMENT:
        return this.marketSegment;
      case COST_BREAKDOWNS.COST_PROFILE:
        return this.costProfiles;
    }
  }

  buildCostBreakdown(
    breakdown: CostBreakdown,
    referenceType: string,
    comparisonLabel: string | null,
  ): CostBreakdownData {
    const total = this.total;
    const source = this.#getBreakdownSource(breakdown);
    const unmapped = source.find((record) => record.isUnmapped);

    // A record whose parent is missing from the payload goes at the top level,
    // so rows still add up to the totals.
    const knownIds = new Set(source.map((record) => record.id));
    const isRoot = (record: NetRevenueCostBreakdown): boolean =>
      record.parentId == null || !knownIds.has(record.parentId);

    // A parent's metrics already include its descendants, so nothing is summed.
    const childrenByParentId = new Map<string, NetRevenueCostBreakdown[]>();
    source.forEach((record) => {
      if (record.isUnmapped || isRoot(record) || record.parentId == null) {
        return;
      }
      const siblings = childrenByParentId.get(record.parentId) ?? [];
      siblings.push(record);
      childrenByParentId.set(record.parentId, siblings);
    });

    const rows: CostTypeRow[] = source
      .filter((record) => !record.isUnmapped && isRoot(record))
      .map((record) =>
        toCostTypeRow(record, referenceType, childrenByParentId),
      );

    return {
      comparisonLabel,
      rows,
      totals: {
        adr: total.adr,
        avgCost: total.avgCost,
        comparison: null,
        cost: total.cost ?? 0,
        costPercent: total.costPercent,
        netAdr: total.netAdr,
        netRevenue: total.netRevenue,
        netRevenuePercent: total.netRevenuePercent,
        roomNights: total.roomNights,
        roomRevenue: total.roomRevenue,
      },
      // Hidden when every reservation is attributed.
      unmapped:
        unmapped && unmapped.roomNights > 0
          ? {
              roomNights: unmapped.roomNights,
              roomRevenue: unmapped.roomRevenue,
            }
          : null,
    };
  }

  buildKpiData(referenceType: string): KpiData {
    const total = this.total;
    const reference = total.referenceByType(referenceType);

    return {
      avgCost: buildKpi(
        total.avgCost,
        reference
          ? (calculateAverageCostPerRoomNight(
              reference.cost ?? 0,
              reference.roomNights,
            ) ?? 0)
          : undefined,
        {
          invertColors: true,
          label: this.intl.t('net_revenue.per_room_night'),
        },
      ),
      cost: buildKpi(
        total.cost ?? 0,
        reference ? (reference.cost ?? 0) : undefined,
        { invertColors: true, percentOfRevenue: total.costPercent },
      ),
      netAdr: buildKpi(
        total.netAdr,
        reference
          ? (calculateNetAdr(reference.netRevenue, reference.roomNights) ?? 0)
          : undefined,
      ),
      netRevenue: buildKpi(total.netRevenue, reference?.netRevenue, {
        percentOfRevenue: total.netRevenuePercent,
      }),
    };
  }

  buildStayData(
    granularity: StayDateGranularity,
    referenceType: string,
    comparisonLabel: string | null,
  ): StayDateData {
    if (granularity === STAY_DATE_GRANULARITIES.DAY) {
      const points: StayDatePoint[] = this.stayDate.map((point) => {
        const reference = point.referenceByType(referenceType);
        const cost = point.cost ?? 0;
        return {
          avgCost:
            calculateAverageCostPerRoomNight(cost, point.roomNights) ?? 0,
          comparison: reference
            ? toStayComparison(reference, point.capacity)
            : null,
          cost,
          costPercent:
            calculateCostToRoomRevenueRatio(cost, point.roomRevenue) ?? 0,
          label: point.date,
          netRevenue: point.netRevenue,
          occupancy: point.occupancy,
          roomRevenue: point.roomRevenue,
        };
      });
      return { comparisonLabel, points };
    }

    return {
      comparisonLabel,
      points: this.#bucketStayDate(granularity, referenceType),
    };
  }
}
